import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  buildPqrFieldNavTarget,
  type PqrFieldDescriptor,
  type PqrFieldLinkContext,
  type PqrFieldNavTarget
} from './pqrFieldLinkRegistry'
import PqrNavigateConfirmModal from './PqrNavigateConfirmModal'

/**
 * Canvas-side wrapper that turns a region of the PQR Preview into a
 * navigate-to-source affordance.
 *
 * Owns:
 *   - Aggregated-mode guard. When `context.weldId` is null we render
 *     the wrapper with `aria-disabled` + a tooltip and ignore clicks
 *     entirely.
 *   - Click → confirm modal → navigate-on-continue. The modal shows
 *     either the clean two-button variant or, when staging is dirty,
 *     the three-button variant.
 *   - URL construction via `buildPqrFieldNavTarget`. The canvas never
 *     builds destination URLs by hand.
 *   - "Don't ask again this session" suppression via `sessionStorage`.
 *     The suppression flag is only honoured while staging is clean —
 *     dirty staging always re-prompts so unsaved edits can't be lost.
 */

/** sessionStorage key for "Don't ask again this session" suppression. */
const SUPPRESSION_STORAGE_KEY = 'wps-studio:pqr-nav-confirm-suppressed'

function isSuppressionFlagSet(): boolean {
  try {
    return sessionStorage.getItem(SUPPRESSION_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

function setSuppressionFlag(): void {
  try {
    sessionStorage.setItem(SUPPRESSION_STORAGE_KEY, '1')
  } catch {
    /* sessionStorage may be unavailable (SSR, privacy mode) — fine. */
  }
}

export interface PqrPreviewFieldLinkActions {
  /** Reports whether the PQR Preview's staging buffer has unsaved edits. */
  isDirty: () => boolean
  /** Discard every staged edit. Used by "Discard & Continue". */
  onDiscardAll: () => void
  /**
   * Save every staged edit. Resolves `true` on success (so the link can
   * navigate) or `false` to keep the user here (e.g. save returned a
   * stale-version error and the page is showing the user the conflict).
   */
  onSaveAll: () => Promise<boolean>
}

/**
 * Shared confirm-modal navigation handler used by both `PqrPreviewFieldLink`
 * (div-based wrapper) and table-row-based links that can't be wrapped in a
 * `<div role="button">` for HTML validity reasons (e.g. `<tr>` inside `<tbody>`).
 *
 * Exposes a pre-computed `target`, a click/keydown handler that opens the
 * confirm modal (or skips it when suppression is active and staging is
 * clean), and the props to feed straight into `PqrNavigateConfirmModal`.
 */
export interface PqrFieldLinkNavigation {
  target: PqrFieldNavTarget | null
  disabled: boolean
  open: boolean
  setOpen: (open: boolean) => void
  /** Wire to onClick on the rendered wrapper element. */
  handleClick: (e: React.MouseEvent) => void
  /** Wire to onKeyDown for keyboard-accessible activation. */
  handleKeyDown: (e: React.KeyboardEvent) => void
  /** Spread directly onto `PqrNavigateConfirmModal`. */
  modalProps: {
    fieldLabel: string
    destinationLabel: string
    weldId: string | null
    dirty: boolean
    onCancel: () => void
    onContinue: (args: { suppressFuture: boolean }) => void
    onDiscardAndContinue: () => void
    onSaveAndContinue: () => Promise<boolean>
  }
}

export function usePqrFieldLinkNavigation(
  descriptor: PqrFieldDescriptor,
  fieldLabel: string,
  context: PqrFieldLinkContext,
  actions?: PqrPreviewFieldLinkActions
): PqrFieldLinkNavigation {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)

  const target = buildPqrFieldNavTarget(descriptor, context)
  const disabled = target === null
  const dirty = actions ? actions.isDirty() : false

  const navigateNow = () => {
    if (!target) return
    navigate(target.fullUrl)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (disabled) return
    if (!dirty && isSuppressionFlagSet()) {
      navigateNow()
      return
    }
    setOpen(true)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      if (!dirty && isSuppressionFlagSet()) {
        navigateNow()
        return
      }
      setOpen(true)
    }
  }

  return {
    target,
    disabled,
    open,
    setOpen,
    handleClick,
    handleKeyDown,
    modalProps: {
      fieldLabel,
      destinationLabel: target?.destinationLabel ?? '',
      weldId: context.weldId,
      dirty,
      onCancel: () => setOpen(false),
      onContinue: ({ suppressFuture }) => {
        if (suppressFuture) setSuppressionFlag()
        setOpen(false)
        navigateNow()
      },
      onDiscardAndContinue: () => {
        actions?.onDiscardAll()
        setOpen(false)
        navigateNow()
      },
      onSaveAndContinue: async () => {
        if (!actions) return false
        const ok = await actions.onSaveAll()
        if (ok) {
          setOpen(false)
          navigateNow()
        }
        return ok
      }
    }
  }
}

interface Props {
  descriptor: PqrFieldDescriptor
  /** Human label for the clicked field, e.g. "IWM Operator". */
  fieldLabel: string
  context: PqrFieldLinkContext
  /**
   * Optional. When supplied the link can negotiate unsaved staged edits
   * via the three-button modal. When omitted the link assumes the buffer
   * is always clean (every section that's been deleted from staging by
   * later slices ends up here automatically).
   */
  actions?: PqrPreviewFieldLinkActions
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  /**
   * Extra data-* attributes to apply to the wrapper. Used by section-level
   * links (e.g. Joint Preparation) that need to preserve the legacy
   * `data-section`/`data-dirty` hooks the canvas tests rely on.
   */
  dataAttributes?: Record<string, string | undefined>
}

export default function PqrPreviewFieldLink({
  descriptor,
  fieldLabel,
  context,
  actions,
  children,
  className,
  style,
  dataAttributes
}: Props) {
  const nav = usePqrFieldLinkNavigation(descriptor, fieldLabel, context, actions)

  return (
    <>
      <div
        role="button"
        tabIndex={nav.disabled ? -1 : 0}
        aria-label={fieldLabel}
        aria-disabled={nav.disabled ? 'true' : undefined}
        title={
          nav.disabled
            ? 'Pick a weld first (use Change Weld) to edit fields from the PQR Preview.'
            : undefined
        }
        onClick={nav.handleClick}
        onKeyDown={nav.handleKeyDown}
        className={className}
        style={{
          cursor: nav.disabled ? 'not-allowed' : 'pointer',
          opacity: nav.disabled ? 0.55 : 1,
          ...style
        }}
        {...(dataAttributes ?? {})}
      >
        {children}
      </div>
      <PqrNavigateConfirmModal open={nav.open} {...nav.modalProps} />
    </>
  )
}
